import { AssessmentType } from 'lib/documents/patient-assessment/patient-assessment';
import {
  createContext,
  PropsWithChildren,
  useContext,
  useMemo,
  useReducer
} from 'react';

/**
 * Represents a single item in the user's cart.
 */
export interface CartItem {
  /** The type of assessment product (e.g., "baseline", "post-injury"). */
  type: AssessmentType;
  /** Quantity of units to purchase. */
  quantity: number;
  /** Price per individual unit before any bulk discounts are applied. */
  pricePerUnit: number;
  /** Flag indicating if this item represents a bulk purchase. */
  isBulk?: boolean;
}

/**
 * Internal representation of a cart item with computed total cost,
 * exposed only to the CartProvider context.
 */
export interface CartItemWithTotal {
  type: AssessmentType;
  quantity: number;
}

export interface CartState {
  /** The items currently in the cart. */
  cart: CartItem[];
  /** Total cost of all items combined, calculated from each item's pricePerUnit × quantity. */
  totalCost?: number;
}

type CartAction =
  | { type: 'ADD_TO_CART'; payload: CartItem }
  | { type: 'REMOVE_FROM_CART'; payload: number }
  | { type: 'UPDATE_CART_ITEM'; payload: { index: number; newItem: CartItem } }
  | { type: 'CLEAR_CART' };

interface CartContextType extends Omit<CartState, 'cart'> {
  cart: CartItem[];
  totalCost?: number;
  dispatch: React.Dispatch<CartAction>;
}

/**
 * Reducer that updates the local cart state. It does not yet perform API calls;
 * those are handled by APIClinicBilling in page-billing-checkout.tsx.
 */
function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case 'ADD_TO_CART':
      return {
        ...state,
        cart: [...state.cart, action.payload]
      };
    case 'REMOVE_FROM_CART':
      return {
        ...state,
        cart: state.cart.filter((_, i) => i !== action.payload)
      };
    case 'UPDATE_CART_ITEM':
      return {
        ...state,
        cart: state.cart.map((item, i) =>
          i === action.payload.index ? action.payload.newItem : item
        )
      };
    case 'CLEAR_CART':
      return {
        ...state,
        cart: []
      };
    default:
      return state;
  }
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider(props: PropsWithChildren<object>) {
  const initialState: CartState = {
    cart: []
  };

  const [state, dispatch] = useReducer(cartReducer, initialState);

  const memoisedState = useMemo(
    () => ({
      ...state,
      dispatch
    }),
    [state]
  );

  return (
    <CartContext.Provider value={memoisedState}>
      {props.children}
    </CartContext.Provider>
  );
}

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
